import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import BoltIcon from '@mui/icons-material/Bolt';
import DeleteIcon from '@mui/icons-material/Delete';
import LoadingButton from '@mui/lab/LoadingButton';
import {
    Alert,
    Box,
    Button,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    Skeleton,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import BackNavigationButton from '../../../components/common/BackNavigationButton';
import LargeMaterialUISwitch from '../../../components/common/LargeMaterialUISwitch';
import OnlyFansListsSelector from '../../../components/common/OnlyFansListsSelector';
import PageContainer from '../../../components/common/PageContainter';
import SliderSetting from '../../../components/common/SliderSetting';
import StyledCard from '../../../components/common/StyledCard';
import StyledCardTitleBar from '../../../components/common/StyledCardTitleBar';
import useSextForceLiveStreamGoals, { LiveStreamGoal } from '../../../hooks/useSextForceLiveStreamGoals';
import useSubscriber from '../../../hooks/useSubscriber';
import { SettingsContext } from '../../../store/SettingsContext';
import { isSextforceActive } from '../../../utils/common';

const makeDefaultGoal = (onlyfansId: number, position: number): LiveStreamGoal => ({
    onlyfansId,
    position,
    tipsGoal: '',
    tipsGoalSum: 50,
    autoKickEnabled: false,
    tippedHoursAgo: 0,
    usersCanWatchMinutes: 10,
    usersCanWatchMinutesOnRejoin: 10,
    numberOfRejoinsAllowed: 1,
    publicShameFan: false,
    sendWarningMessages: true,
    onlySendFinalWarning: false,
    whiteListListId: '0',
    minimumTipAmount: 1,
});

const SextforceLiveStreamGoals = () => {
    const settingsContext = useContext(SettingsContext);
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const { data: subscriber } = useSubscriber();
    const {
        data: savedGoals,
        isLoading,
        refetch,
        createGoal,
        updateGoal,
        deleteGoal,
        reorderGoals,
        setActiveGoal,
        getServiceStatus,
        setServiceStatus,
    } = useSextForceLiveStreamGoals();

    const [goals, setGoals] = useState<LiveStreamGoal[]>([]);
    const [savingIndex, setSavingIndex] = useState<number | null>(null);
    const [deletingIndex, setDeletingIndex] = useState<number | null>(null);
    const [activatingIndex, setActivatingIndex] = useState<number | null>(null);
    const [serviceActive, setServiceActiveState] = useState<boolean>(false);
    const [serviceLoading, setServiceLoading] = useState<boolean>(false);

    const sextforceActive = useMemo(() => !!(subscriber && isSextforceActive(subscriber)), [subscriber]);

    useEffect(() => {
        if (savedGoals) {
            setGoals(savedGoals);
        }
    }, [savedGoals]);

    // Load the Live Stream Goals master on/off status.
    useEffect(() => {
        if (!sextforceActive) {
            setServiceActiveState(false);
            return;
        }
        getServiceStatus()
            .then(active => setServiceActiveState(active))
            .catch(() => setServiceActiveState(false));
    }, [sextforceActive, subscriber?._id]);

    const toggleService = async () => {
        const next = !serviceActive;
        setServiceLoading(true);
        try {
            const ok = await setServiceStatus(next);
            if (ok) {
                setServiceActiveState(next);
                enqueueSnackbar(`Live Stream Goals turned ${next ? 'on' : 'off'}.`, { variant: 'success', autoHideDuration: 2500 });
            } else {
                enqueueSnackbar('Could not update the Live Stream Goals switch.', { variant: 'error', autoHideDuration: 3000 });
            }
        } finally {
            setServiceLoading(false);
        }
    };

    // Reorder is only safe once every goal is persisted (has an id and a real position).
    const hasUnsavedGoal = useMemo(() => goals.some(goal => !goal.id), [goals]);

    const patchGoal = (index: number, patch: Partial<LiveStreamGoal>) => {
        setGoals(prev => prev.map((goal, i) => (i === index ? { ...goal, ...patch } : goal)));
    };

    const addGoal = () => {
        if (!subscriber) {
            return;
        }
        setGoals(prev => [...prev, makeDefaultGoal(subscriber.ofUserId, prev.length)]);
    };

    const saveGoal = async (index: number) => {
        const goal = goals[index];
        setSavingIndex(index);
        try {
            const ok = goal.id ? await updateGoal(goal) : !!(await createGoal(goal));
            if (ok) {
                enqueueSnackbar('Goal saved!', { variant: 'success', autoHideDuration: 2500 });
                await refetch();
            } else {
                enqueueSnackbar('Goal did not save.', { variant: 'error', autoHideDuration: 3000 });
            }
        } finally {
            setSavingIndex(null);
        }
    };

    const removeGoal = async (index: number) => {
        const goal = goals[index];
        if (!goal.id) {
            // Unsaved goal — just drop it locally.
            setGoals(prev => prev.filter((_, i) => i !== index));
            return;
        }
        setDeletingIndex(index);
        try {
            const ok = await deleteGoal(goal.id);
            if (ok) {
                enqueueSnackbar('Goal deleted.', { variant: 'success', autoHideDuration: 2500 });
                await refetch();
            } else {
                enqueueSnackbar('Goal did not delete.', { variant: 'error', autoHideDuration: 3000 });
            }
        } finally {
            setDeletingIndex(null);
        }
    };

    const moveGoal = async (index: number, direction: -1 | 1) => {
        const target = index + direction;
        if (target < 0 || target >= goals.length) {
            return;
        }
        const reordered = [...goals];
        [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
        setGoals(reordered);

        // Persist the new order only when everything is saved.
        if (!reordered.some(goal => !goal.id)) {
            const ok = await reorderGoals(
                subscriber!.ofUserId,
                reordered.map(goal => goal.id as string),
            );
            if (ok) {
                await refetch();
            }
        }
    };

    const activateGoal = async (index: number) => {
        setActivatingIndex(index);
        try {
            const result = await setActiveGoal(index);
            if (result.success) {
                enqueueSnackbar('Live stream goal updated!', { variant: 'success', autoHideDuration: 2500 });
            } else {
                enqueueSnackbar('No live stream is currently running for this creator.', { variant: 'warning', autoHideDuration: 3500 });
            }
        } finally {
            setActivatingIndex(null);
        }
    };

    return (
        <PageContainer>
            {subscriber && '_id' in subscriber && subscriber._id && (
                <>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        <BackNavigationButton url={`/subscribers/${subscriber._id}/${settingsContext.services.sextforce.homeUrl}`} />
                        Live Stream Goals for {subscriber.username}
                    </Typography>

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar title="Live Stream Goals" />
                    </StyledCard>
                    <StyledCard>
                        <Typography gutterBottom>
                            Set up an ordered list of tip goals for your live stream. As each goal's total is reached, the next goal is set
                            automatically so you never have to touch it mid-show.
                        </Typography>
                        <Typography gutterBottom>
                            Each goal has its own Auto Kick settings — keep kicking <strong>off</strong> on early goals so fans can settle
                            in and enjoy, then tighten things up as the goals climb. Goal targets are <strong>cumulative totals</strong>{' '}
                            (e.g. $100, then $250, then $500 raised).
                        </Typography>
                        <Typography gutterBottom>
                            Advancing a goal restarts the free-watch timer for current viewers under the new goal's rules. Whitelisted and
                            blacklisted fans are managed on the{' '}
                            <Link
                                to={`/subscribers/${subscriber._id}/${settingsContext.services.sextforce.homeUrl}/liveStream/autokick`}
                                style={{ textDecoration: 'none', fontWeight: 'bold' }}
                            >
                                Auto Kick Freeloaders
                            </Link>{' '}
                            page and apply across all goals.
                        </Typography>
                        {!sextforceActive && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                                Sextforce is not active for this subscriber, so goals will not run until it is enabled.
                            </Alert>
                        )}
                    </StyledCard>

                    <StyledCard>
                        <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center">
                            <Grid item xs>
                                Live Stream Goals are currently{' '}
                                {serviceActive ? (
                                    <span style={{ color: theme.palette.success.dark, fontWeight: 700 }}>on</span>
                                ) : (
                                    <span style={{ color: theme.palette.error.main, fontWeight: 700 }}>off</span>
                                )}
                                . When on, goals auto-advance as they're reached and any goal with Auto Kick enabled uses its own kick
                                settings.
                            </Grid>
                            <Grid item>
                                <LargeMaterialUISwitch
                                    checked={serviceActive}
                                    disabled={!sextforceActive || serviceLoading}
                                    onChange={toggleService}
                                />
                            </Grid>
                        </Grid>
                    </StyledCard>

                    {isLoading ? (
                        <StyledCard>
                            <Skeleton variant="rectangular" height={120} sx={{ mb: 2 }} />
                            <Skeleton variant="rectangular" height={120} />
                        </StyledCard>
                    ) : (
                        goals.map((goal, index) => (
                            <StyledCard key={goal.id ?? `new-${index}`}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                                    <Typography variant="h6" fontWeight={700}>
                                        Goal {index + 1}
                                        {!goal.id && (
                                            <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1 }}>
                                                (unsaved)
                                            </Typography>
                                        )}
                                    </Typography>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <Tooltip title="Move up">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    disabled={index === 0 || hasUnsavedGoal}
                                                    onClick={() => moveGoal(index, -1)}
                                                >
                                                    <ArrowUpwardIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Move down">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    disabled={index === goals.length - 1 || hasUnsavedGoal}
                                                    onClick={() => moveGoal(index, 1)}
                                                >
                                                    <ArrowDownwardIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Set this goal on the live stream now">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    color="secondary"
                                                    disabled={!goal.id || activatingIndex === index}
                                                    onClick={() => activateGoal(index)}
                                                >
                                                    <BoltIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Delete goal">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    disabled={deletingIndex === index}
                                                    onClick={() => removeGoal(index)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Stack>
                                </Stack>

                                <Grid container spacing={2} mb={2}>
                                    <Grid item xs={12} sm={8}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Goal Label"
                                            placeholder="e.g. Take my top off"
                                            value={goal.tipsGoal}
                                            onChange={e => patchGoal(index, { tipsGoal: e.target.value })}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label="Target Total ($)"
                                            value={goal.tipsGoalSum}
                                            onChange={e => patchGoal(index, { tipsGoalSum: Math.max(1, Number(e.target.value) || 0) })}
                                            inputProps={{ min: 1 }}
                                        />
                                    </Grid>
                                </Grid>

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={goal.autoKickEnabled}
                                            onChange={e => patchGoal(index, { autoKickEnabled: e.target.checked })}
                                        />
                                    }
                                    label="Enable Auto Kick during this goal"
                                />

                                {goal.autoKickEnabled && (
                                    <>
                                        <Divider sx={{ my: 2 }} />
                                        <SliderSetting
                                            label="Tips From How Long Ago Count"
                                            value={goal.tippedHoursAgo}
                                            onChange={val => patchGoal(index, { tippedHoursAgo: val })}
                                            min={0}
                                            max={72}
                                            unit="hours"
                                            helperText="Only tips within this window count as 'paid up'. 0 requires a tip during the current stream."
                                        />
                                        <SliderSetting
                                            label="Minimum Tip Amount"
                                            value={goal.minimumTipAmount}
                                            onChange={val => patchGoal(index, { minimumTipAmount: val })}
                                            min={1}
                                            max={200}
                                            unit="Dollars"
                                            helperText="Tips below this amount don't spare a freeloader. Set to 1 for no minimum."
                                        />
                                        <SliderSetting
                                            label="Total Time Users Can Watch Free"
                                            value={goal.usersCanWatchMinutes}
                                            onChange={val => patchGoal(index, { usersCanWatchMinutes: val })}
                                            min={4}
                                            max={120}
                                            unit="minutes"
                                            helperText="How long a freeloader gets before warnings kick in."
                                        />
                                        <SliderSetting
                                            label="Number Of Rejoins After Being Booted Allowed"
                                            value={goal.numberOfRejoinsAllowed}
                                            onChange={val => patchGoal(index, { numberOfRejoinsAllowed: val })}
                                            min={0}
                                            max={10}
                                            unit="times"
                                            helperText="Give kicked fans a chance to come back — or 0 for a one-strike policy."
                                        />
                                        <SliderSetting
                                            label="Total Time Users Can Watch Free After Rejoining"
                                            value={goal.usersCanWatchMinutesOnRejoin}
                                            onChange={val => patchGoal(index, { usersCanWatchMinutesOnRejoin: val })}
                                            min={4}
                                            max={120}
                                            unit="minutes"
                                            disabled={goal.numberOfRejoinsAllowed === 0}
                                            helperText="How long they get on their second (or third...) chance."
                                        />
                                        <Grid mb={2} pl={1}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={goal.sendWarningMessages}
                                                        onChange={e => patchGoal(index, { sendWarningMessages: e.target.checked })}
                                                    />
                                                }
                                                label="Warn fans before kicking (recommended)"
                                            />
                                        </Grid>
                                        <Grid mb={2} pl={1}>
                                            <FormControlLabel
                                                disabled={!goal.sendWarningMessages}
                                                control={
                                                    <Switch
                                                        checked={goal.onlySendFinalWarning}
                                                        onChange={e => patchGoal(index, { onlySendFinalWarning: e.target.checked })}
                                                    />
                                                }
                                                label="Only send the last warning instead of all 3."
                                            />
                                        </Grid>
                                        <Grid mb={2} pl={1}>
                                            <Typography variant="subtitle2" gutterBottom>
                                                Whitelist an OnlyFans List for this goal. Anyone on it won't be kicked while this goal is
                                                active.
                                            </Typography>
                                            <OnlyFansListsSelector
                                                includeBuiltInLists={false}
                                                targetList={
                                                    goal.whiteListListId && goal.whiteListListId !== '0'
                                                        ? { id: parseInt(goal.whiteListListId, 10), name: '' }
                                                        : undefined
                                                }
                                                setTargetList={targetList => {
                                                    patchGoal(index, { whiteListListId: targetList?.id.toString() ?? '0' });
                                                }}
                                                includeSelectNoneOption={true}
                                                showCreateListButton={true}
                                                size="small"
                                            />
                                        </Grid>
                                    </>
                                )}

                                <Box mt={2}>
                                    <LoadingButton
                                        loading={savingIndex === index}
                                        variant="contained"
                                        fullWidth
                                        onClick={() => saveGoal(index)}
                                    >
                                        <span>{goal.id ? 'Save Goal' : 'Create Goal'}</span>
                                    </LoadingButton>
                                </Box>
                            </StyledCard>
                        ))
                    )}

                    <StyledCard>
                        <Button startIcon={<AddIcon />} variant="outlined" fullWidth onClick={addGoal} disabled={!subscriber}>
                            Add Goal
                        </Button>
                        {hasUnsavedGoal && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Save all goals before reordering them.
                            </Typography>
                        )}
                    </StyledCard>
                </>
            )}
        </PageContainer>
    );
};

export default SextforceLiveStreamGoals;
